/**
 * Finanzübersicht.
 *
 * Eine Managementübersicht, keine Buchhaltungsablage. Wer die Seite zwei
 * Sekunden ansieht, muss zwei Dinge wissen: läuft das Jahr, und sind die
 * Zahlen aktuell. Deshalb steht oben der Datenstand als Monatsstreifen,
 * darunter vier Ampeln, dann eine einzige Kurve. Die Monatszahlen selbst sind
 * eingeklappt – wer sie braucht, klickt einmal.
 *
 * Die Ablage der Buchhaltungsberichte ist hier verschwunden. Eine Dateiliste
 * in einer Auswertung war der Ort, an dem niemand sie sucht; die PDFs liegen
 * in der Ablage, dort gehören sie hin. Geblieben ist die eine Auskunft, die
 * man wirklich braucht: aus welchen Monaten Saldenlisten vorliegen.
 */
import { useMemo, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { Nachrichtenknopf } from '../komponenten/ChatKontext';
import { KoraMaskenhinweise } from '../komponenten/KoraHinweise';
import { useKora } from '../zustand';
import { eur, eurKurz } from '../hilfen/format';
import type {
  Finanzen as FinanzenDaten,
  SaldenAbgleich,
  StreifenZustand,
} from '../typen';
import './finanzen.css';
import { Menueknopf } from '../komponenten/Shell';

/** Ein Feld des Monatsstreifens, nach dem Abgleich mit dem Laufwerk. */
type Streifenfeld = FinanzenDaten['streifen'][number] & { ueberfaellig: boolean };

/** Die Aussage über dem Streifen: Farbe, Satz und – wenn möglich – eine Tat. */
interface Aussage {
  farbe: 'gruen' | 'gelb' | 'rot';
  satz: string;
  /** Beschriftung des Knopfes rechts. Null heißt: hier ist nichts zu tun. */
  knopf: string | null;
  /** Welcher Monat eingelesen würde. Null bei mehreren – dann das ganze Jahr. */
  monat: string | null;
}

/** „August, September und Oktober 2026" – der Jahresteil steht nur einmal. */
function monateSatz(felder: Streifenfeld[]): string {
  const jahr = felder[0]?.label.split(' ')[1] ?? '';
  const namen = felder.map((f) => f.label.split(' ')[0] ?? '');
  const aufzaehlung =
    namen.length <= 1
      ? (namen[0] ?? '')
      : `${namen.slice(0, -1).join(', ')} und ${namen[namen.length - 1]}`;
  return `${aufzaehlung} ${jahr}`;
}

/**
 * Was der Datenstand sagt.
 *
 * Immer eine Aussage, nie ein Feldname: „Alles eingelesen bis Juli 2026" statt
 * „Letzter Bericht: Juli". Eine Managementübersicht spricht in Sätzen.
 *
 * `geprueft` ist der wichtigste Parameter: Ohne erfolgreichen Blick aufs
 * Laufwerk darf hier nie stehen, dort liege nichts. Das wäre nicht geprüft,
 * sondern verschwiegen.
 */
function datenstandAussage(felder: Streifenfeld[], jahr: string, geprueft: boolean): Aussage {
  const inAblage = felder.filter((f) => f.zustand === 'inAblage');
  const fehlend = felder.filter((f) => f.zustand === 'fehlt');
  const eingelesen = felder.filter((f) => f.zustand === 'eingelesen');
  const ueberfaellig = fehlend.filter((f) => f.ueberfaellig);

  if (inAblage.length) {
    const einer = inAblage.length === 1 ? inAblage[0]! : null;
    return {
      farbe: 'gelb',
      satz: einer
        ? `${einer.label} liegt in der Ablage, ist aber noch nicht eingelesen.`
        : `${monateSatz(inAblage)} liegen in der Ablage, sind aber noch nicht eingelesen.`,
      knopf: einer
        ? `${einer.label.split(' ')[0]} einlesen`
        : `${inAblage.length} Monate einlesen`,
      monat: einer ? einer.monat : null,
    };
  }

  if (ueberfaellig.length) {
    return {
      farbe: 'rot',
      satz:
        `Für ${monateSatz(ueberfaellig)} ${ueberfaellig.length === 1 ? 'fehlt die Saldenliste' : 'fehlen die Saldenlisten'}` +
        (geprueft ? ' – auch in der Ablage liegt nichts.' : '.'),
      knopf: null,
      monat: null,
    };
  }

  if (fehlend.length) {
    return {
      farbe: 'gelb',
      satz:
        `${monateSatz(fehlend)} ${fehlend.length === 1 ? 'steht' : 'stehen'} noch aus` +
        (geprueft ? ' – in der Ablage liegt bisher nichts.' : '.'),
      knopf: null,
      monat: null,
    };
  }

  return {
    farbe: 'gruen',
    satz: eingelesen.length
      ? `Alles eingelesen bis ${eingelesen[eingelesen.length - 1]!.label}.`
      : `Für ${jahr} ist noch keine Saldenliste eingelesen.`,
    knopf: null,
    monat: null,
  };
}

export function Finanzen() {
  const { toastZeigen } = useKora();
  const queryClient = useQueryClient();

  const [zeitraum, setZeitraum] = useState<'jahr' | 'monat' | 'vorjahr'>('jahr');
  const [importOffen, setImportOffen] = useState(false);
  const [importJahr, setImportJahr] = useState<number | null>(null);
  const [salden, setSalden] = useState<SaldenAbgleich | null>(null);
  const [tabelleOffen, setTabelleOffen] = useState(false);
  /* Die Vorjahreslinie ist aus, bis jemand sie holt – siehe Verlaufskurve. */
  const [vorjahrZeigen, setVorjahrZeigen] = useState(false);
  const [fehler, setFehler] = useState('');

  const finanzen = useQuery({
    queryKey: ['finanzen', zeitraum],
    queryFn: () => api.hole<FinanzenDaten>(`/finanzen?zeitraum=${zeitraum}`),
  });

  /**
   * Der Blick aufs Laufwerk.
   *
   * Getrennt von den Zahlen, weil er je Monat einen Abruf kostet: Die Seite
   * soll sofort dastehen und den Fund nachtragen, nicht umgekehrt.
   */
  const neueListen = useQuery({
    queryKey: ['salden-neu'],
    queryFn: () =>
      api.hole<{ aktiv: boolean; neu: { monat: string; datei: string }[] }>(
        '/finanzen/salden/neu',
      ),
    staleTime: 60_000,
  });

  /** Erst zeigen, was herauskäme, dann übernehmen. Bei Geldbeträgen erst recht. */
  const saldenPruefen = useMutation({
    mutationFn: (jahr: number) =>
      api.hole<SaldenAbgleich>(`/finanzen/salden/abgleich?jahr=${jahr}`),
    onSuccess: (a) => {
      setFehler('');
      setImportOffen(false);
      setSalden(a);
    },
    onError: (f) => setFehler(f instanceof Error ? f.message : 'Abgleich fehlgeschlagen.'),
  });

  const saldenUebernehmen = useMutation({
    mutationFn: (auswahl: { jahr?: number; monat?: string }) =>
      api.sende<{ uebernommen: number; uebersprungen: number; gruende: string[] }>(
        '/finanzen/salden/uebernehmen',
        auswahl,
      ),
    onSuccess: (a) => {
      void queryClient.invalidateQueries({ queryKey: ['finanzen'] });
      void queryClient.invalidateQueries({ queryKey: ['salden-neu'] });
      /*
       * Kora muss mit.
       *
       * Ihr Satz gilt sonst für die ganze Sitzung – wer gerade eingelesen hat
       * und daneben weiter „Die Zahlen sind nicht aktuell" liest, sucht den
       * Fehler beim Import. Genau das, worüber sie sich beklagt hat, ist eben
       * behoben worden.
       */
      void queryClient.invalidateQueries({ queryKey: ['kora-satz'] });
      setSalden(null);
      setFehler('');
      toastZeigen(
        a.uebernommen
          ? `${a.uebernommen} ${a.uebernommen === 1 ? 'Monat' : 'Monate'} übernommen.` +
              (a.uebersprungen ? ` ${a.uebersprungen} ohne auswertbare Liste.` : '')
          : a.gruende[0] || 'Keine auswertbare Saldenliste gefunden.',
      );
    },
    onError: (f) => setFehler(f instanceof Error ? f.message : 'Übernahme fehlgeschlagen.'),
  });

  const d = finanzen.data;

  /**
   * Der Streifen, nachgeschärft.
   *
   * Der Server weiß nur, was in Kora steht. Was zusätzlich auf dem Laufwerk
   * liegt, kommt aus /salden/neu und wird hier eingetragen – erst dann ist der
   * Streifen vollständig, und erst dann stimmt die Aussage darüber.
   */
  const streifen = useMemo<Streifenfeld[]>(() => {
    const roh = (d?.streifen ?? []) as Streifenfeld[];
    const gefunden = new Set((neueListen.data?.neu ?? []).map((n) => n.monat));
    return roh.map((f) =>
      f.zustand !== 'eingelesen' && gefunden.has(f.monat)
        ? { ...f, zustand: 'inAblage' as StreifenZustand }
        : f,
    );
  }, [d, neueListen.data]);

  const aussage = useMemo(
    () => datenstandAussage(streifen, d?.jahr ?? '', !!neueListen.data),
    [streifen, d, neueListen.data],
  );

  const laeuft = saldenUebernehmen.isPending || saldenPruefen.isPending;

  return (
    <div className="fin-seite">
      {/*
        Kopf: links die Aussage, rechts die einzige Aktion dieser Seite.
        Sie muss man finden können, ohne zu scrollen – deshalb oben rechts und
        in Markenrot, nicht als grauer Zweitknopf in einer Seitenspalte.
      */}
      <div className="fin-kopf">
        <div className="eh-kopf-links">
          <Menueknopf />
          <div>
          <div className="fin-augenbraue">Kaufmännisches</div>
          <h1 className="fin-titel">Finanzen</h1>
          <div className="fin-unterzeile">
            Aus den Saldenlisten der Buchhaltung, ohne Umsatzsteuer.
          </div>
        </div>

        </div>
        <div className="fin-kopf-rechts eh-kopf-rechts">
          <Nachrichtenknopf />

          {/*
            Der Stapel steht neben dem Symbol, nicht darunter: Die rechte
            Kopfgruppe ist eine Reihe (v11, 10.2). Was innerhalb davon
            untereinander gehoert - Aktion oben, Zeitraum darunter - steht
            in diesem Stapel.
          */}
          <div className="fin-kopf-stapel">
            <div className="fin-importbereich">
            <button
              type="button"
              className="fin-hauptknopf"
              onClick={() => {
                setImportJahr(d?.importJahre[0] ?? new Date().getFullYear());
                setImportOffen(!importOffen);
              }}
              disabled={laeuft || neueListen.data?.aktiv === false}
            >
              {/* Ein Pfeil nach unten: Zahlen kommen von außen herein. */}
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path
                  d="M8 2v8m0 0 3-3m-3 3L5 7M2.5 12.5h11"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {saldenPruefen.isPending ? 'Wird gelesen …' : 'Zahlen aus Saldenlisten holen'}
            </button>

            {importOffen && (
              <div className="fin-importwahl">
                <div className="fin-importwahl-text">Welches Jahr soll Kora einlesen?</div>
                <div className="fin-importwahl-jahre">
                  {(d?.importJahre ?? []).map((j) => (
                    <button
                      key={j}
                      type="button"
                      className={`fin-segment ${importJahr === j ? 'aktiv' : ''}`}
                      onClick={() => setImportJahr(j)}
                    >
                      {j}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="fin-importwahl-los"
                  disabled={saldenPruefen.isPending || importJahr === null}
                  onClick={() => importJahr !== null && saldenPruefen.mutate(importJahr)}
                >
                  {saldenPruefen.isPending ? 'Wird gelesen …' : `${importJahr} vom Laufwerk lesen`}
                </button>
                <div className="fin-importwahl-fuss">
                  Kora liest jede Saldenliste des Jahres, rechnet sie gegen ihre eigene
                  Ergebniszeile und zeigt das Ergebnis, bevor etwas übernommen wird.
                </div>
              </div>
            )}
          </div>

          {/*
            Zeitraum unter der Aktion, nicht daneben: Er ändert, was man sieht,
            nicht, was man tut. Drei Fälle genügen – Saldenlisten kommen
            monatsweise, ein freier von–bis-Wähler hätte hier keinen Sinn.
          */}
          <div className="fin-zeitraumleiste">
            {(d?.auswahl ?? []).map((w) => (
              <button
                key={w.schluessel}
                type="button"
                className={`fin-segment ${zeitraum === w.schluessel ? 'aktiv' : ''}`}
                onClick={() => setZeitraum(w.schluessel as 'jahr' | 'monat' | 'vorjahr')}
              >
                {w.label}
              </button>
            ))}
          </div>
          </div>
        </div>
      </div>

      {fehler && <div className="fin-fehler">{fehler}</div>}

      {finanzen.isLoading && <div className="fin-karte">Zahlen werden geladen …</div>}

      {d && (
        <>
          {/* ---------- Datenstand ---------- */}
          {neueListen.isError ? (
            <div className="fin-stand">
              <div className="fin-stand-kopf">
                <div className="fin-stand-satz">
                  <span className="fin-punkt gelb" />
                  Kora konnte nicht nachsehen, ob neuere Saldenlisten bereitliegen.
                </div>
                <button
                  type="button"
                  className="fin-stand-knopf"
                  onClick={() => void neueListen.refetch()}
                  disabled={neueListen.isFetching}
                >
                  {neueListen.isFetching ? 'Wird geprüft …' : 'Nochmal prüfen'}
                </button>
              </div>
              <Monatsstreifen felder={streifen} />
              <div className="fin-stand-fuss">
                {neueListen.error instanceof Error ? neueListen.error.message : ''} Der Streifen
                zeigt nur, was in Kora steht.
              </div>
            </div>
          ) : (
            <div className="fin-stand">
              <div className="fin-stand-kopf">
                <div className="fin-stand-satz">
                  <span className={`fin-punkt ${aussage.farbe}`} />
                  {aussage.satz}
                </div>
                {aussage.knopf && (
                  <button
                    type="button"
                    className="fin-stand-knopf"
                    disabled={laeuft}
                    onClick={() =>
                      saldenUebernehmen.mutate(
                        aussage.monat
                          ? { monat: aussage.monat }
                          : { jahr: Number(d.jahr) },
                      )
                    }
                  >
                    {saldenUebernehmen.isPending ? 'Wird eingelesen …' : aussage.knopf}
                  </button>
                )}
              </div>
              <Monatsstreifen felder={streifen} />
            </div>
          )}

          {/* ---------- Der Abgleich, wenn einer läuft ---------- */}
          {salden && (
            <Abgleichkarte
              salden={salden}
              laeuft={saldenUebernehmen.isPending}
              onSchliessen={() => setSalden(null)}
              onUebernehmen={() =>
                saldenUebernehmen.mutate(
                  importJahr !== null ? { jahr: importJahr } : {},
                )
              }
            />
          )}

          {/* ---------- Die vier Ampeln ---------- */}
          <div className="fin-ampeln">
            {d.ampeln.map((a) => (
              <div className={`fin-ampel ${a.farbe}`} key={a.schluessel}>
                <div className="fin-ampel-kopf">
                  <span className="fin-ampel-label">{a.label}</span>
                  {a.info && <Infoknopf text={a.info} zu={a.label} />}
                </div>
                <div className="fin-ampel-zeile">
                  <span className="fin-ampel-wert">{a.wert}</span>
                  {a.zusatz && <span className="fin-ampel-zusatz">{a.zusatz}</span>}
                </div>
                <div className="fin-ampel-satz">{a.satz}</div>
                {/*
                  Die Regel gehört in die Karte. Eine Ampel ohne genannte
                  Schwelle ist eine Meinung; mit Schwelle ist sie eine Kennzahl.
                */}
                <div className="fin-ampel-regel">{a.regel}</div>
              </div>
            ))}
          </div>

          {/* ---------- Die eine Kurve ---------- */}
          <div className="fin-karte">
            <div className="fin-kurve-kopf">
              <div>
                <div className="fin-label">{d.kurve.titel}</div>
                <div className="fin-kurve-unterzeile">{d.kurve.unterzeile}</div>
              </div>
              <div className="fin-kurve-steuerung">
                <div className="fin-legende">
                  <span>
                    <span className="fin-legende-strich umsatz" />
                    Umsatz
                  </span>
                  <span>
                    <span className="fin-legende-strich ergebnis" />
                    Gewinn
                  </span>
                  {vorjahrZeigen && (
                    <span>
                      <span className="fin-legende-strich vorjahr" />
                      {d.kurve.vorjahrLabel}
                    </span>
                  )}
                </div>

                {/*
                  Das Vorjahr ist ein Angebot, keine Vorgabe.

                  2025 begann erst im Juli – als Maßstab taugt es nicht, und
                  eine Linie, die nichts vergleicht, verstellt nur das Bild.
                  Ab 2027 gegen 2026 wird sie zur eigentlichen Aussage; bis
                  dahin holt sie sich, wer sie sehen will.
                */}
                {d.kurve.vorjahr.length > 0 && (
                  <label className="fin-vorjahr-schalter">
                    <input
                      type="checkbox"
                      checked={vorjahrZeigen}
                      onChange={(e) => setVorjahrZeigen(e.target.checked)}
                    />
                    {d.kurve.vorjahrLabel} einblenden
                  </label>
                )}
              </div>
            </div>
            <Verlaufskurve kurve={d.kurve} jahr={d.jahr} mitVorjahr={vorjahrZeigen} />
          </div>

          {/* ---------- Monate im Detail ---------- */}
          <Monatstabelle
            tabelle={d.tabelle}
            offen={tabelleOffen}
            onUmschalten={() => setTabelleOffen(!tabelleOffen)}
          />
        </>
      )}
      {/* KORA steht unter dem Inhalt der Maske, nicht darüber (v11, 10.2 Regel 6). */}
      <KoraMaskenhinweise maske="finanzen" label="Finanzen" />
    </div>
  );
}

/**
 * Ein kleines i, hinter dem die Erklärung steckt.
 *
 * Was eine Zahl bedeutet, liest man einmal und danach nie wieder. Als
 * Dauertext unter der Karte kostet es jeden Tag Platz und Aufmerksamkeit; hier
 * holt es sich, wer es braucht.
 *
 * Der Knopf reagiert auf Zeiger und Tastatur gleichermaßen – ein Hinweis, der
 * nur bei Mauskontakt erscheint, ist für die Tastatur gar nicht vorhanden.
 */
function Infoknopf({ text, zu }: { text: string; zu: string }) {
  const [offen, setOffen] = useState(false);
  return (
    <span
      className="fin-info"
      onMouseEnter={() => setOffen(true)}
      onMouseLeave={() => setOffen(false)}
    >
      <button
        type="button"
        className="fin-info-knopf"
        aria-label={`Was bedeutet „${zu}“?`}
        aria-expanded={offen}
        onClick={() => setOffen(!offen)}
        onFocus={() => setOffen(true)}
        onBlur={() => setOffen(false)}
        onKeyDown={(e) => e.key === 'Escape' && setOffen(false)}
      >
        i
      </button>
      {offen && (
        <span className="fin-info-blase" role="tooltip">
          {text}
        </span>
      )}
    </span>
  );
}

/**
 * Der Monatsstreifen.
 *
 * Zwölf Felder, ein Blick: Was steckt in diesen Zahlen? Gefüllt heißt
 * eingelesen, gestrichelt heißt „liegt bereit", leer heißt fehlt, blass heißt
 * nicht erwartet – künftige Monate ebenso wie solche vor dem ersten je
 * erfassten. Die Legende steht darunter, weil vier Zustände sich nicht von
 * selbst erklären.
 */
function Monatsstreifen({ felder }: { felder: Streifenfeld[] }) {
  return (
    <>
      <div className="fin-streifen">
        {felder.map((f) => (
          <div className="fin-streifen-feld" key={f.monat} title={f.label}>
            <div className={`fin-streifen-balken ${f.zustand}`} />
            <div className={`fin-streifen-name ${f.zustand}`}>{f.kurz}</div>
          </div>
        ))}
      </div>
      <div className="fin-streifen-legende">
        <span>
          <span className="fin-streifen-probe eingelesen" />
          eingelesen
        </span>
        <span>
          <span className="fin-streifen-probe inAblage" />
          in der Ablage, nicht eingelesen
        </span>
        <span>
          <span className="fin-streifen-probe fehlt" />
          fehlt
        </span>
        <span>
          <span className="fin-streifen-probe offen" />
          nicht erwartet
        </span>
      </div>
    </>
  );
}

/**
 * Die kumulierte Entwicklung.
 *
 * Umsatz und Gewinn auf einer Achse mit Nulllinie, die Vorjahreslinie auf
 * Wunsch dazu. Sie ist standardmäßig aus: 2025 begann erst im Juli und ist als
 * Maßstab nicht brauchbar – eine Linie, die nichts vergleicht, verstellt nur
 * das Bild. Ab 2027 gegen 2026 wird sie zur eigentlichen Aussage.
 *
 * Die Gewinnlinie endet am letzten eingelesenen Monat. Sie auf null fallen
 * zu lassen hieße zu behaupten, ab August sei nichts mehr verdient worden.
 */
function Verlaufskurve({
  kurve,
  jahr,
  mitVorjahr,
}: {
  kurve: FinanzenDaten['kurve'];
  jahr: string;
  mitVorjahr: boolean;
}) {
  const B = 1000;
  const H = 380;
  const links = 66;
  const rechts = 14;
  const oben = 14;
  const unten = 34;

  /** Welcher Monat gerade unter dem Zeiger liegt – null heißt keiner. */
  const [aktiv, setAktiv] = useState<number | null>(null);

  const x = (i: number) => links + (i / 11) * (B - links - rechts);

  /*
   * Die Spanne wird hier gerechnet, nicht auf dem Server: Sie hängt davon ab,
   * ob die Vorjahreslinie sichtbar ist. Die Null ist immer dabei – ohne sie
   * verlöre ein Verlust seinen Bezugspunkt.
   */
  const werte = [
    0,
    ...kurve.punkte.flatMap((p) => [p.umsatz, p.ergebnis]),
    ...(mitVorjahr ? kurve.vorjahr.map((p) => p.ergebnis) : []),
  ];

  /**
   * Die Achse reicht bis eine Million.
   *
   * Fest und nicht mitwachsend: Nur so bedeutet dieselbe Steigung im Mai
   * dasselbe wie im November und 2027 dasselbe wie 2026. Der Preis ist eine
   * flachere Kurve, solange das Jahr jung ist – das ist die Aussage und kein
   * Mangel. Liegt der Umsatz darüber, wächst die Achse mit; abgeschnitten
   * wird nie.
   */
  const ZIEL = 1_000_000;
  const rohMax = Math.max(ZIEL, ...werte);
  const rohMin = Math.min(0, ...werte);

  /*
   * Glatte Schritte, damit die Beschriftung lesbar bleibt und die Nulllinie
   * auf einer Rasterlinie liegt. Höchstens sechs Abschnitte – mehr Raster als
   * Kurve liest niemand.
   */
  const schritt =
    [100_000, 200_000, 250_000, 500_000, 1_000_000].find(
      (s) => (rohMax - rohMin) / s <= 6,
    ) ?? 1_000_000;
  const max = Math.ceil(rohMax / schritt) * schritt;
  const min = Math.floor(rohMin / schritt) * schritt;
  const spanne = Math.max(1, max - min);
  const y = (v: number) => oben + (1 - (v - min) / spanne) * (H - oben - unten);

  const pfad = <T extends { i: number }>(punkte: T[], wert: (p: T) => number) =>
    punkte.map((p, k) => `${k ? 'L' : 'M'}${x(p.i).toFixed(1)} ${y(wert(p)).toFixed(1)}`).join(' ');

  const stufen = Array.from(
    { length: Math.round(spanne / schritt) + 1 },
    (_, k) => min + k * schritt,
  );

  const gezeigt = aktiv === null ? null : (kurve.punkte.find((p) => p.i === aktiv) ?? null);
  const gezeigtVorjahr =
    aktiv === null || !mitVorjahr ? null : (kurve.vorjahr.find((p) => p.i === aktiv) ?? null);

  return (
    <div className="fin-kurve-feld">
      <svg
        className="fin-kurve"
        viewBox={`0 0 ${B} ${H}`}
        role="img"
        aria-label={kurve.unterzeile}
        onMouseLeave={() => setAktiv(null)}
      >
        {/* Was noch nicht eingelesen ist, liegt hinter neutralem Grau. */}
        {kurve.abIndex <= 11 && (
          <>
            <rect
              x={x(Math.max(0, kurve.abIndex - 0.5))}
              y={oben}
              width={Math.max(0, B - rechts - x(Math.max(0, kurve.abIndex - 0.5)))}
              height={H - oben - unten}
              fill="var(--eh-kurve-blass)"
            />
            <text
              x={(x(Math.max(0, kurve.abIndex - 0.5)) + (B - rechts)) / 2}
              y={oben + 26}
              className="fin-kurve-blassnotiz"
              textAnchor="middle"
            >
              noch nicht eingelesen
            </text>
          </>
        )}

        {stufen.map((wert, k) => (
          <g key={k}>
            <line
              x1={links}
              x2={B - rechts}
              y1={y(wert)}
              y2={y(wert)}
              stroke="var(--eh-kurve-raster)"
              strokeWidth={1}
            />
            <text x={links - 12} y={y(wert) + 5} className="fin-kurve-achse" textAnchor="end">
              {eurKurz(wert)}
            </text>
          </g>
        ))}

        {/* Die Nulllinie trägt die Aussage „Verlust" und ist deshalb kräftiger. */}
        {min < 0 && max > 0 && (
          <line
            x1={links}
            x2={B - rechts}
            y1={y(0)}
            y2={y(0)}
            stroke="var(--eh-text-3)"
            strokeWidth={1.5}
          />
        )}

        {/*
          Das Vorjahr wird gestrichelt gezeichnet, nicht nur blasser. Zwei
          Linien allein über den Farbton zu trennen scheitert bei jedem, der
          Farben schlecht unterscheidet – und bei jedem Ausdruck.
        */}
        {mitVorjahr && (
          <path
            d={pfad(kurve.vorjahr, (p: { ergebnis: number }) => p.ergebnis)}
            fill="none"
            stroke="var(--eh-kurve-vorjahr)"
            strokeWidth={3}
            strokeDasharray="9 6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
        <path
          d={pfad(kurve.punkte, (p: { umsatz: number }) => p.umsatz)}
          fill="none"
          stroke="var(--eh-kurve-umsatz)"
          strokeWidth={3.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <path
          d={pfad(kurve.punkte, (p: { ergebnis: number }) => p.ergebnis)}
          fill="none"
          stroke="var(--eh-kurve-ergebnis)"
          strokeWidth={3.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/* Der weiße Ring hebt den Punkt von der eigenen Linie ab. */}
        {kurve.punkte.map((p) => (
          <g key={p.i} className={aktiv === p.i ? 'fin-kurve-punkt aktiv' : 'fin-kurve-punkt'}>
            <circle
              cx={x(p.i)}
              cy={y(p.umsatz)}
              r={aktiv === p.i ? 7.5 : 5.5}
              fill="var(--eh-kurve-umsatz)"
              stroke="var(--eh-karte)"
              strokeWidth={2}
            />
            <circle
              cx={x(p.i)}
              cy={y(p.ergebnis)}
              r={aktiv === p.i ? 7.5 : 5.5}
              fill="var(--eh-kurve-ergebnis)"
              stroke="var(--eh-karte)"
              strokeWidth={2}
            />
          </g>
        ))}

        {kurve.monate.map((m, i) => (
          <text
            key={m}
            x={x(i)}
            y={H - 10}
            className={`fin-kurve-monat ${i === kurve.abIndex - 1 ? 'stand' : ''}`}
            textAnchor="middle"
          >
            {m}
          </text>
        ))}

        {/*
          Unsichtbare Spalten fangen den Zeiger ab. Auf einen 5-px-Punkt zu
          zielen wäre eine Geduldsprobe; die ganze Spalte ist die Fläche, die
          jemand meint, wenn er auf einen Monat schaut.

          Darunter liegt eine Fläche, die den Hinweis wieder wegnimmt. Ohne sie
          bliebe er am letzten Monat kleben, während der Zeiger längst über
          Oktober steht – und behauptete etwas über eine Stelle, auf die
          niemand zeigt.
        */}
        <rect
          x={links}
          y={oben}
          width={B - links - rechts}
          height={H - oben - unten}
          fill="transparent"
          onMouseEnter={() => setAktiv(null)}
        />
        {kurve.punkte.map((p) => (
          <rect
            key={`z${p.i}`}
            x={x(p.i) - (B - links - rechts) / 22}
            y={oben}
            width={(B - links - rechts) / 11}
            height={H - oben - unten}
            fill="transparent"
            onMouseEnter={() => setAktiv(p.i)}
          />
        ))}
      </svg>

      {gezeigt && (
        <div
          className="fin-kurve-hinweis"
          style={{
            left: `${(x(gezeigt.i) / B) * 100}%`,
            transform: gezeigt.i > 6 ? 'translateX(-100%) translateX(-14px)' : 'translateX(14px)',
          }}
        >
          <div className="fin-kurve-hinweis-monat">
            {kurve.monate[gezeigt.i]} {jahr} · aufsummiert
          </div>
          <div className="fin-kurve-hinweis-zeile">
            <span className="fin-legende-strich umsatz" />
            Umsatz
            <strong>{eur(gezeigt.umsatz)}</strong>
          </div>
          <div className="fin-kurve-hinweis-zeile">
            <span className="fin-legende-strich ergebnis" />
            Gewinn
            <strong>{eur(gezeigt.ergebnis)}</strong>
          </div>
          {gezeigtVorjahr && (
            <div className="fin-kurve-hinweis-zeile">
              <span className="fin-legende-strich vorjahr" />
              {kurve.vorjahrLabel}
              <strong>{eur(gezeigtVorjahr.ergebnis)}</strong>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Monate im Detail – eingeklappt.
 *
 * Zahlen liest man besser als Balkenhöhen; das Balkendiagramm ist deshalb
 * ersatzlos entfallen. Wer die Übersicht braucht, sieht diese Tabelle nicht;
 * wer die Details braucht, klickt einmal.
 */
function Monatstabelle({
  tabelle,
  offen,
  onUmschalten,
}: {
  tabelle: FinanzenDaten['tabelle'];
  offen: boolean;
  onUmschalten: () => void;
}) {
  return (
    <div className={`fin-detail ${offen ? 'offen' : ''}`}>
      <button type="button" className="fin-detail-kopf" onClick={onUmschalten}>
        <span className="fin-label">Monate im Detail</span>
        <span className="fin-detail-anzahl">{tabelle.anzahlText}</span>
        <span className="fin-detail-schalter">{offen ? 'Zuklappen ▲' : 'Aufklappen ▼'}</span>
      </button>

      {offen && (
        <div className="fin-detail-tabelle">
          <div className="fin-detail-zeile fin-detail-titelzeile">
            <div>Monat</div>
            <div>Umsatz</div>
            <div>Ausgaben</div>
            <div>Gewinn</div>
            <div>Marge</div>
          </div>
          {tabelle.zeilen.map((z) => (
            <div className="fin-detail-zeile" key={z.monat}>
              <div className="fin-detail-monat">
                <span className={`fin-punkt ${z.farbe}`} />
                {z.label}
              </div>
              <div className="fin-detail-zahl">{z.umsatz}</div>
              <div className="fin-detail-zahl">{z.ausgaben}</div>
              <div className={`fin-detail-zahl ergebnis ${z.minus ? 'minus' : ''}`}>
                {z.ergebnis}
              </div>
              <div className={`fin-detail-zahl ${z.minus ? 'minus' : ''}`}>{z.marge}</div>
            </div>
          ))}
          {tabelle.zeilen.length > 0 && (
            <div className="fin-detail-zeile fin-detail-summe">
              <div className="fin-detail-monat">{tabelle.summe.label}</div>
              <div className="fin-detail-zahl">{tabelle.summe.umsatz}</div>
              <div className="fin-detail-zahl">{tabelle.summe.ausgaben}</div>
              <div className={`fin-detail-zahl ergebnis ${tabelle.summe.minus ? 'minus' : ''}`}>
                {tabelle.summe.ergebnis}
              </div>
              <div className={`fin-detail-zahl ${tabelle.summe.minus ? 'minus' : ''}`}>
                {tabelle.summe.marge}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Was ein Einlesen ergäbe – vor dem Einlesen.
 *
 * Jeder Monat wurde gegen die Ergebniszeile seiner eigenen Saldenliste
 * gerechnet. Übernommen wird nur, was übereinstimmt; alles andere trägt hier
 * seinen Grund. Ein Knopf, der ungefragt Geldbeträge schreibt, ist unangenehm.
 */
function Abgleichkarte({
  salden,
  laeuft,
  onSchliessen,
  onUebernehmen,
}: {
  salden: SaldenAbgleich;
  laeuft: boolean;
  onSchliessen: () => void;
  onUebernehmen: () => void;
}) {
  const lesbar = salden.monate.filter((m) => m.lesbar);
  return (
    <div className="fin-karte fin-arbeitsflaeche">
      <div className="fin-karte-kopf">
        <div className="fin-label">Saldenlisten</div>
        <button type="button" className="fin-textknopf" onClick={onSchliessen} disabled={laeuft}>
          Schließen
        </button>
      </div>
      <div className="fin-hinweis">
        Jeder Monat wurde gegen die Ergebniszeile seiner eigenen Saldenliste gerechnet. Übernommen
        wird nur, was übereinstimmt.
      </div>

      <div className="fin-salden-tabelle">
        <div className="fin-salden-zeile fin-salden-kopf">
          <div>Monat</div>
          <div>Umsatz</div>
          <div>Ausgaben</div>
          <div>Gewinn</div>
          <div>Stand</div>
          <div />
        </div>
        {salden.monate.map((m) => (
          <div className="fin-salden-zeile" key={m.monat}>
            <div className="fin-salden-monat">{m.label}</div>
            {m.lesbar ? (
              <>
                <div className="fin-salden-zahl">{m.umsatzText}</div>
                <div className="fin-salden-zahl">{m.ausgabenText}</div>
                <div className={`fin-salden-zahl ${(m.gewinn ?? 0) < 0 ? 'minus' : ''}`}>
                  {m.gewinnText}
                </div>
                <div className="fin-salden-stand">{m.stand ?? '—'}</div>
                <div className={`fin-salden-marke ${m.aenderung}`}>{m.aenderung}</div>
              </>
            ) : (
              <div className="fin-salden-hinweis">{m.hinweis}</div>
            )}
          </div>
        ))}
      </div>

      {salden.jahre
        .filter((j) => j.monate > 0)
        .map((j) => (
          <div className="fin-salden-jahr" key={j.jahr}>
            <strong>{j.jahr}</strong>: Summe der {j.monate} Monate {j.summeMonate}, laut jüngster
            Liste {j.kumuliert} seit Jahresbeginn.
            {Math.abs(j.abweichendeCent) > 100 && (
              <>
                {' '}
                Der Unterschied von {j.abweichung} kommt von Nachbuchungen in bereits gedruckten
                Monaten – die kumulierte Zahl ist die aktuellere.
              </>
            )}
          </div>
        ))}

      {/*
        Steckt im Gewinn eine Ertragsteuer?

        Die Frage entscheidet, wie die Zahl auf dieser Seite heißen darf. Sie
        lässt sich nur an der Saldenliste selbst beantworten – deshalb steht
        die Antwort dort, wo die Liste gerade gelesen wurde.
      */}
      {salden.klasse8Satz && <div className="fin-salden-jahr">{salden.klasse8Satz}</div>}

      <button
        type="button"
        className="fin-uebernehmen"
        onClick={onUebernehmen}
        disabled={laeuft || lesbar.length === 0}
      >
        {laeuft
          ? 'Wird übernommen …'
          : `${lesbar.length} ${lesbar.length === 1 ? 'Monat' : 'Monate'} übernehmen`}
      </button>
    </div>
  );
}

